import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export const STATUS_OPTIONS = [
  { value: 'watching',  label: 'Viendo',     icon: '👁️' },
  { value: 'completed', label: 'Completado', icon: '✅' },
  { value: 'paused',    label: 'En pausa',   icon: '⏸️' },
  { value: 'dropped',   label: 'Dropeado',   icon: '❌' },
  { value: 'pending',   label: 'Pendiente',  icon: '📋' },
];

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
};

// expanded=true  → all 5 buttons visible (AnimePage)
// expanded=false → dropdown triggered by a single button (card overlay)
export default function AnimeStatusSelector({
  animeId,
  currentStatus: initialStatus,
  onStatusChange,
  expanded = false,
}) {
  const { user } = useAuth();
  const [status,    setStatus]    = useState(initialStatus ?? undefined);
  const [open,      setOpen]      = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [loginMsg,  setLoginMsg]  = useState(false);
  const dropRef = useRef(null);

  // Sync when parent passes a new currentStatus (e.g. AnimePage re-mounts)
  useEffect(() => {
    if (initialStatus !== undefined) setStatus(initialStatus);
  }, [initialStatus]);

  // Lazy-fetch status the first time the dropdown opens (if not pre-loaded)
  useEffect(() => {
    if (!open || !user || status !== undefined) return;
    authFetch(`/user/anime-status/${encodeURIComponent(animeId)}`)
      .then((r) => r.json())
      .then((d) => setStatus(d.status || null))
      .catch(() => setStatus(null));
  }, [open, user, animeId, status]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = async (e, value) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      setLoginMsg(true);
      setTimeout(() => setLoginMsg(false), 2500);
      return;
    }

    const optimistic = status === value ? null : value;
    setStatus(optimistic);
    setLoading(true);
    try {
      const r = await authFetch('/user/anime-status', {
        method: 'POST',
        body: JSON.stringify({ animeId, status: value }),
      });
      const data = await r.json();
      setStatus(data.status);
      onStatusChange?.(data.status);
    } catch {
      setStatus(status); // revert
    } finally {
      setLoading(false);
      if (!expanded) setOpen(false);
    }
  };

  const activeOption = STATUS_OPTIONS.find((o) => o.value === status);

  if (expanded) {
    return (
      <div className={`ase ase--expanded${loading ? ' ase--loading' : ''}`}>
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`ase__btn${status === opt.value ? ' ase__btn--active' : ''}`}
            onClick={(e) => handleSelect(e, opt.value)}
            title={opt.label}
          >
            <span className="ase__icon">{opt.icon}</span>
            <span className="ase__label">{opt.label}</span>
          </button>
        ))}
        {loginMsg && (
          <span className="ase__login-msg">Iniciá sesión para guardar tu lista</span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={dropRef}
      className={`ase ase--dropdown${loading ? ' ase--loading' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className={`ase__trigger${activeOption ? ' ase__trigger--has-status' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={activeOption ? activeOption.label : 'Agregar a mi lista'}
      >
        {activeOption ? activeOption.icon : '+'}
      </button>

      {open && (
        <div className="ase__popover">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`ase__popover-item${status === opt.value ? ' ase__popover-item--active' : ''}`}
              onClick={(e) => handleSelect(e, opt.value)}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
          {loginMsg && (
            <p className="ase__login-msg">Iniciá sesión para guardar tu lista</p>
          )}
        </div>
      )}
    </div>
  );
}
